import type { ReactNode } from 'react';
import type { AdminSystem } from '../api.ts';
import { ClockIcon, FolderIcon, RefreshIcon, ShieldIcon, SparklesIcon } from '../Icon.tsx';
import { Loading } from './States.tsx';

function formatWhen(ms: number | null): string {
  if (ms === null) return '—';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(ms));
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

export function AdminSystemPanel({ system }: { system: AdminSystem | null }): ReactNode {
  if (system === null) return <Loading label="Sistem özeti yükleniyor…" />;
  const fs = system.disk.filesystem;
  return (
    <section className="admin__panel" aria-label="Sistem özeti">
      <div className={`admin__system-state admin__system-state--${system.state}`}>
        <ShieldIcon size={16} />
        <strong>
          {system.state === 'ok'
            ? 'Sistem sağlıklı'
            : system.state === 'warning'
              ? 'Dikkat gerekiyor'
              : 'Kritik durum'}
        </strong>
        {system.warnings.length > 0 && <span>{system.warnings.join(' · ')}</span>}
      </div>

      <div className="admin__system-grid">
        <article className="admin__system-card">
          <h3>
            <FolderIcon size={16} /> Disk
          </h3>
          <dl>
            <div>
              <dt>Dosya sistemi</dt>
              <dd>
                {fs === null
                  ? 'Okunamadı'
                  : `${formatBytes(fs.freeBytes)} boş / ${formatBytes(fs.totalBytes)}`}
              </dd>
            </div>
            <div>
              <dt>Transcode önbelleği</dt>
              <dd>{formatBytes(system.disk.transcode.usedBytes)}</dd>
            </div>
            <div>
              <dt>Uzak önbellek</dt>
              <dd>
                {system.disk.remoteCache === null
                  ? 'Yok'
                  : formatBytes(system.disk.remoteCache.usedBytes)}
              </dd>
            </div>
          </dl>
        </article>

        <article className="admin__system-card">
          <h3>
            <ClockIcon size={16} /> Yedek
          </h3>
          <dl>
            <div>
              <dt>Durum</dt>
              <dd>
                {system.backup.state}
                {system.backup.stale ? ' · eski' : ''}
              </dd>
            </div>
            <div>
              <dt>Son yedek</dt>
              <dd>{system.backup.latest ?? 'Yok'}</dd>
            </div>
            <div>
              <dt>Yaş</dt>
              <dd>{system.backup.ageHours === null ? '—' : `${system.backup.ageHours} saat`}</dd>
            </div>
          </dl>
        </article>

        <article className="admin__system-card">
          <h3>
            <RefreshIcon size={16} /> Kuyruklar
          </h3>
          <dl>
            <div>
              <dt>Transcode çalışan</dt>
              <dd>
                {system.queues.transcode.running} / {system.queues.transcode.concurrency}
              </dd>
            </div>
            <div>
              <dt>Transcode bekleyen</dt>
              <dd>{system.queues.transcode.queued}</dd>
            </div>
            <div>
              <dt>Başarısız</dt>
              <dd>{system.queues.transcode.failed}</dd>
            </div>
            <div>
              <dt>Metadata</dt>
              <dd>
                {system.queues.metadata === null
                  ? 'Kapalı'
                  : `${system.queues.metadata.pending} bekleyen`}
              </dd>
            </div>
          </dl>
        </article>

        <article className="admin__system-card">
          <h3>
            <SparklesIcon size={16} /> Katalog
          </h3>
          <dl>
            <div>
              <dt>Kütüphane</dt>
              <dd>{system.catalog.libraries}</dd>
            </div>
            <div>
              <dt>Son tarama</dt>
              <dd>{formatWhen(system.scans.latestAt)}</dd>
            </div>
            <div>
              <dt>Aktif oturum</dt>
              <dd>{system.sessions.active}</dd>
            </div>
          </dl>
        </article>
      </div>
    </section>
  );
}
